import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { useSnackbar } from 'notistack';
import {
  Button,
  Typography,
  Box,
  Paper,
  TextField,
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DescriptionIcon from '@material-ui/icons/Description';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { makeStyles } from '@material-ui/core/styles';
import { changeBranch } from 'api/kazpension';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '70%',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '30px',
    },
    dropzone: {
        borderRadius: 16,
        border: '1px dashed #D1D5DB',
        backgroundColor: '#FFFFFF',
        padding: theme.spacing(4),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        textAlign: 'center',
    },
    hiddenInput: {
        display: 'none',
    },
    uploadedFileRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    uploadedFile: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: theme.spacing(1.5, 2),
        borderRadius: 12,
        backgroundColor: '#F3F4F6',
        gap: theme.spacing(1),
    },
    errorText: {
        marginTop: theme.spacing(1),
        color: '#EF4444',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    iinErrorsBox: {
        marginTop: theme.spacing(1),
        padding: theme.spacing(1, 1.5),
        borderRadius: 8,
        backgroundColor: '#FEF2F2',
    },
    iinErrorRow: {
        fontSize: 13,
        color: '#B91C1C',
    },
    delete: {
        color: theme.palette.error.main,
    },
}));

const normalizeHeader = (v: any) => String(v ?? '').trim().toLowerCase();

const Pension: React.FC = () => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [file, setFile] = useState<File | null>(null);
    const [branchCode, setBranchCode] = useState('');
    const [iins, setIins] = useState<string[]>([]);
    const [isEditing, setIsEditing] = useState(false);

    const clearFile = () => {
        setFile(null);
        setIins([]);
    };

    const parseExcel = async (f: File) => {
        setIins([]);

        try {
            const buf = await f.arrayBuffer();
            const wb = XLSX.read(buf);
            const sheet = wb.Sheets[wb.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json<any[]>(sheet, {
                header: 1,
                defval: '',
            });

            if (!rows.length) {
                enqueueSnackbar('Файл пустой или не содержит данных.', { variant: 'error', });
                clearFile();
                return;
            }

            const [header, ...body] = rows;

            const iinIndex = header.findIndex(
                (h) => normalizeHeader(h) === 'иин получателя'
            );

            if (iinIndex === -1) {
                enqueueSnackbar('В файле не найдена колонка "ИИН получателя".', { variant: 'error', });
                clearFile();
                return;
            }

            const extracted: string[] = body
                .map((row) => String(row[iinIndex] ?? '').trim())
                .filter((v) => v.length > 0);

            if (!extracted.length) {
                enqueueSnackbar('В колонке "ИИН получателя" нет значений.', { variant: 'error', });
                clearFile();
                return;
            }

            setIins(extracted);
        } catch (e) {
            console.error(e);
            enqueueSnackbar('Не удалось прочитать файл .xlsx.', { variant: 'error', });
            clearFile();
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0] || null;
        setIins([]);

        if (!f) {
            clearFile();
            return;
        }

        if (!f.name.endsWith('.xlsx')) {
            enqueueSnackbar('Поддерживаются только файлы .xlsx.', { variant: 'error', });
            clearFile();
            return;
        }

        setFile(f);
        parseExcel(f);
    };

    const handleBranchCodeChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        setBranchCode(e.target.value);
    };

    const handleDeleteFile = () => {
        clearFile();
        enqueueSnackbar('Файл удалён.', { variant: 'info' });
    };

    const handleEditClick = async () => {
        const trimmedCode = branchCode.trim();

        if (!trimmedCode) {
            enqueueSnackbar('Введите код подразделения.', { variant: 'error' });
            return;
        }
        if (!iins.length) {
            enqueueSnackbar('Сначала загрузите файл с ИИН получателя.', { variant: 'error', });
            return;
        }

        setIsEditing(true);

        try {
            const res = await changeBranch(trimmedCode, iins);
            const data = res.data;

            if (data.status === 'ERROR') {
                enqueueSnackbar(
                    data.message ||
                        'Код подразделения некорректен или не существует.',
                    { variant: 'error' }
                );
                clearFile();
                return;
            }

        if (Array.isArray(data.notFoundIins) && data.notFoundIins.length > 0) {
            enqueueSnackbar(
                    `Следующие ИИН не найдены или некорректны: ${data.notFoundIins.join(
                        ', '
                )}`,
                {
                    variant: 'warning',
                }
            );
            clearFile();
        } else {
            enqueueSnackbar('Успешно отредактировано', { variant: 'success', });
        }
        } catch (e) {
            console.error(e);
            enqueueSnackbar('Не удалось обратиться к сервису.', { variant: 'error',});
            clearFile();
        } finally {
            setIsEditing(false);
        }
    };

    const isEditDisabled =
        !file || !iins.length || !branchCode.trim() || isEditing;

    return (
        <Box className={classes.root}>
            <Typography
                variant="h5"
                component="h1"
                gutterBottom
            >
                Изменение ИИН
            </Typography>

            <Box>
                {!file && (
                    <label className={classes.dropzone}>
                        <input
                            type="file"
                            accept=".xlsx"
                            className={classes.hiddenInput}
                            onChange={handleFileChange}
                        />
                        <Box display="flex" flexDirection="column" alignItems="center">
                        <CloudUploadIcon />
                        <Typography variant="body2" color="textSecondary">
                            Нажмите, чтобы выбрать файл, или перетащите его сюда
                        </Typography>
                        </Box>
                    </label>
                )}

                {file && (
                    <Box className={classes.uploadedFileRow}>
                        <Paper elevation={0} className={classes.uploadedFile}>
                            <DescriptionIcon color="primary" />
                            <Typography variant="body2">{file.name}</Typography>
                        </Paper>

                        <Button
                            variant="text"
                            className={classes.delete}
                            startIcon={<DeleteOutlineIcon />}
                            onClick={handleDeleteFile}
                        >
                            Удалить файл
                        </Button>
                    </Box>
                )}
            </Box>

            <Box display="flex" flexDirection="column" gridGap={20}>
                <TextField
                    label="Код подразделения"
                    variant="outlined"
                    size="small"
                    value={branchCode}
                    onChange={handleBranchCodeChange}
                    fullWidth
                />

                <Button
                    variant="contained"
                    color="primary"
                    disabled={isEditDisabled}
                    onClick={handleEditClick}
                >
                    {isEditing ? 'Обработка…' : 'Редактировать'}
                </Button>
            </Box>
        </Box>
    );
};

export default Pension;
